'use es6';

import { List, fromJS, OrderedSet } from 'immutable';
import { AGGREGATION } from '../../../constants/configTypes';
import { makeFilters } from '../filters';
import { DESC } from '../../../constants/sortOrder';
import * as DataTypes from '../../../constants/dataTypes';
import { PERCENTILES, SUM, COUNT } from '../../../constants/metricTypes';
import { UNIFIED } from '../../../constants/dataSources';
import { buildDrilldownReport } from '../report';
import { ANALYTICS_BEHAVIORAL_EVENTS, ANALYTICS_SOURCES } from '../../../constants/dataTypes';
const defaultProperties = fromJS({
  [DataTypes.ANALYTICS_SOURCES]: ['visits', 'contacts', 'customers', 'bounceRate', 'timePerSession'],
  [DataTypes.ANALYTICS_ALL_PAGES]: ['rawViews', 'submissions', 'contacts', 'pageBounceRate', 'LANGUAGE', 'URL', 'CONTENT_TYPE_CODE']
});
const getPeopleColumns = dataType => {
  switch (dataType) {
    case 'ANALYTICS_FORMS':
    case 'ANALYTICS_FORMS_SOURCES':
    case 'ANALYTICS_FORMS_URLS':
      return [];
    default:
      return ['contacts', 'customers', 'lastTouchContacts', 'influencedContacts', 'influencedDeals', 'submissions', 'uniquePrimaryObjects'];
  }
};
const getPeopleMetrics = dataType => {
  switch (dataType) {
    case ANALYTICS_SOURCES:
      return ['lifecyclestage', 'hs_analytics_source', 'first_conversion_event_name', 'first_conversion_date', 'recent_conversion_event_name', 'recent_conversion_date'];
    case ANALYTICS_BEHAVIORAL_EVENTS:
      return ['isContact', 'completions', 'lifecyclestage', 'hs_analytics_source', 'first_conversion_event_name', 'first_conversion_date', 'recent_conversion_event_name', 'recent_conversion_date'];
    default:
      return [];
  }
};
const getNextUnifiedDrilldown = (report, spec) => {
  const dProperties = ['d1', 'd2'];
  const dFilters = report.getIn(['config', 'filters', 'custom'], List()).filter(filter => dProperties.includes(filter.get('property')));
  const nextD = `d${dFilters.count() + 1}`;
  return spec[nextD] ? nextD : null;
};
const getMetrics = spec => {
  return fromJS(spec.metrics).reduce((acc, type, property) => acc.push(fromJS({
    property,
    metricTypes: [type === 'percent' ? PERCENTILES : SUM]
  })), List()).concat(fromJS(spec.metadata || {}).keySeq().map(property => fromJS({
    property,
    metricTypes: [COUNT]
  })));
};
const getUnifiedDrilldown = (report, keys, options, dataTypeModule) => {
  return dataTypeModule.getUnifiedSpecForConfig({
    config: report.get('config').toJS()
  }).then(spec => {
    const metadataFields = Object.keys(spec.metadata || {});
    const allMetrics = getMetrics(spec);
    const dimensions = report.getIn(['config', 'dimensions']);
    const clickedMetric = keys.get(dimensions.count());
    const isPeople = !dimensions.isEmpty() && !dimensions.equals(List(['sessionDate'])) && getPeopleColumns(report.getIn(['config', 'dataType'])).includes(clickedMetric);
    const metricProperties = report.getIn(['config', 'metrics']).map(m => m.get('property'));
    const dataTypeDefaults = defaultProperties.get(report.getIn(['config', 'dataType'])) || List();
    const properties = OrderedSet().concat(clickedMetric, dataTypeDefaults, metricProperties);
    const peopleMetrics = OrderedSet(getPeopleMetrics(report.getIn(['config', 'dataType'])));
    const metrics = !isPeople ? properties.map(prop => allMetrics.find(metric => metric.get('property') === prop)).filter(x => x).toJS() : peopleMetrics.map(metric => ({
      property: metric,
      metricTypes: [COUNT]
    })).toJS();
    if (dimensions.isEmpty()) {
      const sort = metrics.length > 0 ? [{
        property: metrics[0].property,
        order: DESC
      }] : undefined;
      return buildDrilldownReport(report, report.get('config').merge({
        configType: AGGREGATION,
        limit: 5,
        dimensions: List(['breakdown']),
        metrics,
        sort
      }), options);
    }
    return makeFilters(report.get('config'), keys, options).then(filters => {
      const canAddD = filters.get('custom').find(filter => dimensions.includes(filter.get('property')));

      // metadata properties can't persist into a deeper level
      const usableMetrics = canAddD && !isPeople ? metrics.filter(metric => metric.metricTypes[0] !== COUNT) : metrics;
      const sortProperty = usableMetrics.length > 0 && !isPeople ? usableMetrics[0].property : clickedMetric;
      const updatedFilters = filters.update('custom', List(), customFilters => customFilters.filterNot(filter => canAddD && [...metadataFields, 'filters'].includes(filter.get('property'))).map(filter => {
        if (dimensions.includes(filter.get('property'))) {
          const nextD = getNextUnifiedDrilldown(report, spec);
          return filter.set('property', nextD && !isPeople ? nextD : 'filters');
        }
        return filter;
      }).concat(isPeople ? fromJS([{
        property: 'peopleType',
        value: clickedMetric
      }]) : []));
      const peopleDimension = clickedMetric === 'influencedDeals' ? 'deals-influenced' : 'people';
      return report.get('config').merge(fromJS({
        configType: AGGREGATION,
        limit: 5,
        dimensions: List([isPeople ? peopleDimension : 'breakdown']),
        filters: updatedFilters,
        sort: [{
          property: sortProperty,
          order: DESC
        }],
        metrics: usableMetrics
      }));
    }).then(config => buildDrilldownReport(report, config, options));
  });
};
const getCanDrilldown = (report, dataTypeModule) => {
  if (!dataTypeModule || dataTypeModule.get('dataSource') !== UNIFIED) {
    return false;
  }
  const dimensions = report.getIn(['config', 'dimensions']);
  return dataTypeModule.getUnifiedSpecForConfig({
    config: report.get('config').toJS()
  }).then(spec => {
    if (dimensions.some(dim => ['people', 'deals-influenced'].includes(dim))) {
      return false;
    }
    return !dimensions.includes('breakdown') || dimensions.includes('sessionDate') || getNextUnifiedDrilldown(report, spec);
  });
};
const getIsColumnSupported = (report, column, dataTypeModule) => {
  if (!dataTypeModule || !dataTypeModule.get('dataSource') === UNIFIED) {
    return false;
  }
  return getPeopleColumns(report.getIn(['config', 'dataType'])).includes(column);
};
export default {
  isSupported: getCanDrilldown,
  isSupportedColumn: getIsColumnSupported,
  generate: getUnifiedDrilldown
};